// 028A — Atención al Niño-Niña Menor de 2 Meses (Serie 028, Fase 2, ver
// PLAN.md / tasks/investigacion-028-aiepi-denver.md). Antes vivía como
// Questionnaire genérico (028A.json) sin ningún campo AIEPI real — se
// convierte a Patrón B (discreto) porque el contenido oficial (antecedentes
// familiares/prenatales/natales/postnatales extensos + 2 consultas por
// página, cada una con AIEPI de 9 ítems + examen físico + hasta 3
// diagnósticos con marca) no tiene soporte del lado Patrón A — mismo
// criterio que 038/033.
//
// Fuente primaria: páginas 116-118 del PDF oficial AM-00115-2021 (ver
// tasks/investigacion-028-aiepi-denver.md). Bloque "F/K — Datos del
// profesional responsable" de cada página NO se captura acá: nombre,
// matrícula y firma los arma el servidor al firmar (mismo criterio que el
// resto de los formularios discretos — ver PanelFirmaImpresion).
//
// Único contenido con recursos FHIR reales: cada diagnóstico no vacío de las
// 2 consultas (hasta 6 en total) crea un `Condition` — mismo patrón que 038.
// El resto (antecedentes, AIEPI, clasificación, plan) es narrativo (`Par[]`).
import type { JSX } from 'react';
import { useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { ConsultaCard, GRID_DOS_CONSULTAS } from './DosConsultasLayout';
import { codeDeDiagnostico, diagnosticoConMarcaVacio, SelectorDiagnosticoConMarca, textoDiagnosticoConMarca, type DiagnosticoConMarca, type OpcionMarca } from './MarcaDiagnostico';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { SiNoBotones } from './camposSimples';
import { leerSiNo, type SiNoValor } from './camposSimplesDatos';
import { AIEPI_MENOR_2_MESES, ITEM_SI_NO_OBS_VACIO, paresSiNoObs, paresSiNoSimples, type ItemSiNoObs, ANTECEDENTES_NATALES_028A as ANTECEDENTES_NATALES, ANTECEDENTES_POSTNATALES_SINO_028A as ANTECEDENTES_POSTNATALES_SINO } from './bloques028Datos';

const CODIGO = '028A';

/** E. Postnatales: SI/NO impreso junto a tipificación sanguínea y exámenes especiales (PDF p. 116); el texto va aparte. */
const SI_NO_NACIMIENTO = ['tipificacionSanguinea', 'examenesEspeciales'] as const;

const ANTECEDENTES_FAMILIARES = ['cardiopatia', 'hipertension', 'enfermedadCVascular', 'endocrinoMetabolico', 'cancer', 'tuberculosis', 'enfermedadMental', 'enfermedadInfecciosa', 'malformacion', 'otro'] as const;
const CONDICION_EDAD_OPCIONES = ['D', 'M'] as const;
const TIPO_CONSULTA_OPCIONES = ['primera', 'reEvaluacion'] as const;


interface DatosNacimiento {
  apgar1: string;
  apgar5: string;
  apgar10: string;
  apgar15: string;
  apgar20: string;
  pesoNacer: string;
  tallaNacer: string;
  perimetroCefalicoNacer: string;
  edadGestacionalExamenFisico: string;
  clasificacionRn: string;
  tipificacionSanguinea: string;
  examenesEspeciales: string;
  condicionEgreso: string;
}
const DATOS_NACIMIENTO_VACIO: DatosNacimiento = {
  apgar1: '',
  apgar5: '',
  apgar10: '',
  apgar15: '',
  apgar20: '',
  pesoNacer: '',
  tallaNacer: '',
  perimetroCefalicoNacer: '',
  edadGestacionalExamenFisico: '',
  clasificacionRn: '',
  tipificacionSanguinea: '',
  examenesEspeciales: '',
  condicionEgreso: '',
};

interface ConstantesVitales {
  temperatura: string;
  axilar: string;
  bucal: string;
  digital: string;
  pulso: string;
  frecuenciaRespiratoria: string;
  pulsioximetria: string;
  perimetroCefalico: string;
  peso: string;
  longitud: string;
  presionArterial: string;
  gananciaPeso: string;
}
const CONSTANTES_VACIAS: ConstantesVitales = {
  temperatura: '',
  axilar: '',
  bucal: '',
  digital: '',
  pulso: '',
  frecuenciaRespiratoria: '',
  pulsioximetria: '',
  perimetroCefalico: '',
  peso: '',
  longitud: '',
  presionArterial: '',
  gananciaPeso: '',
};

interface ConsultaAiepiMenor2Meses {
  fecha: string;
  edad: string;
  condicionEdad: string;
  informante: string;
  constantes: ConstantesVitales;
  tipoConsulta: string;
  motivoConsultaTexto: string;
  enfermedadActual: string;
  aiepi: Record<string, SiNoValor>;
  resumenHallazgos: string;
  clasificacionAiepi: string;
  diagnosticos: DiagnosticoConMarca[];
  planTratamiento: string;
  proximaCitaIdeal: string;
}
function consultaVacia(): ConsultaAiepiMenor2Meses {
  return {
    fecha: '',
    edad: '',
    condicionEdad: '',
    informante: '',
    constantes: { ...CONSTANTES_VACIAS },
    tipoConsulta: '',
    motivoConsultaTexto: '',
    enfermedadActual: '',
    aiepi: {},
    resumenHallazgos: '',
    clasificacionAiepi: '',
    diagnosticos: [diagnosticoConMarcaVacio(), diagnosticoConMarcaVacio(), diagnosticoConMarcaVacio()],
    planTratamiento: '',
    proximaCitaIdeal: '',
  };
}

function ListaSiNoObservacion({
  claves,
  prefijoI18n,
  prefijoId,
  valores,
  onChange,
}: {
  claves: readonly string[];
  prefijoI18n: string;
  prefijoId: string;
  valores: Record<string, ItemSiNoObs>;
  onChange: (v: Record<string, ItemSiNoObs>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      {claves.map((clave) => {
        const item = valores[clave] ?? ITEM_SI_NO_OBS_VACIO;
        return (
          <div key={clave} className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-center">
            <div className="flex items-center gap-2">
              <SiNoBotones
                id={`${prefijoId}-${clave}`}
                etiqueta={t(`${prefijoI18n}.${clave}`)}
                valor={leerSiNo(item.valor)}
                textos={{ si: t('formulario.si'), no: t('formulario.no') }}
                onChange={(v) => onChange({ ...valores, [clave]: { ...item, valor: v } })}
              />
            </div>
            <CampoDictado
              id={`${prefijoId}-${clave}-obs`}
              etiqueta={t('formularios.f028a.antecedentes.observacion')}
              valor={item.observacion}
              onChange={(v) => onChange({ ...valores, [clave]: { ...item, observacion: v } })}
            />
          </div>
        );
      })}
    </div>
  );
}

function CampoNumero({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function Formulario028A({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f028a.titulo');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);

  const [antecedentesFamiliares, setAntecedentesFamiliares] = useState<Record<string, boolean>>({});
  const [detalleFamiliares, setDetalleFamiliares] = useState('');

  const [datosObstetricos, setDatosObstetricos] = useState('');
  const [gestas, setGestas] = useState('');
  const [abortos, setAbortos] = useState('');
  const [partos, setPartos] = useState('');
  const [cesareas, setCesareas] = useState('');
  const [hijosVivos, setHijosVivos] = useState('');
  const [hijosMuertos, setHijosMuertos] = useState('');

  const [antecedentesNatales, setAntecedentesNatales] = useState<Record<string, ItemSiNoObs>>({});
  const [observacionesNatales, setObservacionesNatales] = useState('');
  const [antecedentesPostnatalesSiNo, setAntecedentesPostnatalesSiNo] = useState<Record<string, ItemSiNoObs>>({});
  const [siNoNacimiento, setSiNoNacimiento] = useState<Record<string, SiNoValor>>({});
  const [datosNacimiento, setDatosNacimiento] = useState<DatosNacimiento>(DATOS_NACIMIENTO_VACIO);

  const [consulta1, setConsulta1] = useState<ConsultaAiepiMenor2Meses>(() => consultaVacia());
  const [consulta2, setConsulta2] = useState<ConsultaAiepiMenor2Meses>(() => consultaVacia());

  const textosSiNo = { si: t('formulario.si'), no: t('formulario.no') };
  const marcasDiagnostico: readonly OpcionMarca[] = [
    { valor: 'PRE', texto: t('formularios.f028a.marcaDiagnostico.pre') },
    { valor: 'DEF', texto: t('formularios.f028a.marcaDiagnostico.def') },
  ];

  function BloqueConsulta({ numero, value, onChange }: { numero: 1 | 2; value: ConsultaAiepiMenor2Meses; onChange: (v: ConsultaAiepiMenor2Meses) => void }): JSX.Element {
    return (
      <ConsultaCard titulo={t('formularios.f028a.consulta', { numero })}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="flex flex-col gap-1">
            <Label>{t('formularios.f028a.fecha')}</Label>
            <Input type="date" value={value.fecha} onChange={(e) => onChange({ ...value, fecha: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>{t('formularios.f028a.edad')}</Label>
            <Input value={value.edad} onChange={(e) => onChange({ ...value, edad: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>{t('formularios.f028a.condicionEdad')}</Label>
            <Select value={value.condicionEdad || undefined} onValueChange={(condicionEdad) => onChange({ ...value, condicionEdad })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDICION_EDAD_OPCIONES.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`formularios.f028a.condicionEdadOpciones.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <CampoDictado id={`f028a-consulta-${numero}-informante`} etiqueta={t('formularios.f028a.informante')} valor={value.informante} onChange={(v) => onChange({ ...value, informante: v })} />
          </div>
        </div>

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.constantes.titulo')}</h5>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(Object.keys(CONSTANTES_VACIAS) as (keyof ConstantesVitales)[]).map((campo) => (
            <CampoNumero
              key={campo}
              label={t(`formularios.f028a.constantes.${campo}`)}
              value={value.constantes[campo]}
              onChange={(v) => onChange({ ...value, constantes: { ...value.constantes, [campo]: v } })}
            />
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f028a.motivoConsulta')}</Label>
          <Select value={value.tipoConsulta || undefined} onValueChange={(tipoConsulta) => onChange({ ...value, tipoConsulta })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPO_CONSULTA_OPCIONES.map((op) => (
                <SelectItem key={op} value={op}>
                  {t(`formularios.f028a.tipoConsultaOpciones.${op}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AreaDictado
            id={`f028a-consulta-${numero}-motivo-detalle`}
            etiqueta={t('formularios.f028a.motivoConsultaDetalle')}
            valor={value.motivoConsultaTexto}
            onChange={(v) => onChange({ ...value, motivoConsultaTexto: v })}
            filas={2}
          />
        </div>

        <AreaDictado
          id={`f028a-consulta-${numero}-enfermedad-actual`}
          etiqueta={t('formularios.f028a.enfermedadActual')}
          valor={value.enfermedadActual}
          onChange={(v) => onChange({ ...value, enfermedadActual: v })}
          filas={2}
        />

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.seccionAiepi')}</h5>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          {AIEPI_MENOR_2_MESES.map((clave) => (
            <SiNoBotones
              key={clave}
              id={`aiepi-${numero}-${clave}`}
              etiqueta={t(`formularios.f028a.aiepi.${clave}`)}
              valor={leerSiNo(value.aiepi[clave])}
              textos={{ si: t('formulario.si'), no: t('formulario.no') }}
              onChange={(v) => onChange({ ...value, aiepi: { ...value.aiepi, [clave]: v } })}
            />
          ))}
        </div>

        <AreaDictado
          id={`f028a-consulta-${numero}-resumen-hallazgos`}
          etiqueta={t('formularios.f028a.resumenHallazgos')}
          valor={value.resumenHallazgos}
          onChange={(v) => onChange({ ...value, resumenHallazgos: v })}
          filas={2}
        />

        <AreaDictado
          id={`f028a-consulta-${numero}-clasificacion-aiepi`}
          etiqueta={t('formularios.f028a.clasificacionAiepi')}
          valor={value.clasificacionAiepi}
          onChange={(v) => onChange({ ...value, clasificacionAiepi: v })}
          filas={2}
        />

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.seccionDiagnosticos')}</h5>
        <div className="flex flex-col gap-2">
          {value.diagnosticos.map((dx, i) => (
            <SelectorDiagnosticoConMarca
              key={i}
              label={`${t('formularios.f028a.diagnostico')} ${i + 1}`}
              marcaLabel={t('formularios.f028a.marcaDiagnostico.label')}
              marcas={marcasDiagnostico}
              value={dx}
              onChange={(nuevo) => onChange({ ...value, diagnosticos: value.diagnosticos.map((d, idx) => (idx === i ? nuevo : d)) })}
            />
          ))}
        </div>

        <AreaDictado
          id={`f028a-consulta-${numero}-plan-tratamiento`}
          etiqueta={t('formularios.f028a.planTratamiento')}
          valor={value.planTratamiento}
          onChange={(v) => onChange({ ...value, planTratamiento: v })}
          filas={2}
        />
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f028a.proximaCitaIdeal')}</Label>
          <Input type="date" value={value.proximaCitaIdeal} onChange={(e) => onChange({ ...value, proximaCitaIdeal: e.target.value })} />
        </div>
      </ConsultaCard>
    );
  }

  function paresDeConsulta(consulta: ConsultaAiepiMenor2Meses, numero: 1 | 2): Par[] {
    const prefijo = t('formularios.f028a.consulta', { numero });
    return [
      { pregunta: `${prefijo} — ${t('formularios.f028a.fecha')}`, respuesta: consulta.fecha },
      { pregunta: `${prefijo} — ${t('formularios.f028a.edad')}`, respuesta: [consulta.edad, consulta.condicionEdad].filter(Boolean).join(' ') },
      { pregunta: `${prefijo} — ${t('formularios.f028a.informante')}`, respuesta: consulta.informante },
      {
        pregunta: `${prefijo} — ${t('formularios.f028a.constantes.titulo')}`,
        respuesta: (Object.keys(CONSTANTES_VACIAS) as (keyof ConstantesVitales)[])
          .filter((c) => consulta.constantes[c])
          .map((c) => `${t(`formularios.f028a.constantes.${c}`)}: ${consulta.constantes[c]}`)
          .join(' | '),
      },
      { pregunta: `${prefijo} — ${t('formularios.f028a.motivoConsulta')}`, respuesta: consulta.tipoConsulta ? t(`formularios.f028a.tipoConsultaOpciones.${consulta.tipoConsulta}`) : '' },
      { pregunta: `${prefijo} — ${t('formularios.f028a.motivoConsultaDetalle')}`, respuesta: consulta.motivoConsultaTexto.trim() },
      { pregunta: `${prefijo} — ${t('formularios.f028a.enfermedadActual')}`, respuesta: consulta.enfermedadActual.trim() },
      ...paresSiNoSimples(AIEPI_MENOR_2_MESES, consulta.aiepi, (c) => `${prefijo} — ${t('formularios.f028a.seccionAiepi')} — ${t(`formularios.f028a.aiepi.${c}`)}`, textosSiNo),
      { pregunta: `${prefijo} — ${t('formularios.f028a.resumenHallazgos')}`, respuesta: consulta.resumenHallazgos.trim() },
      { pregunta: `${prefijo} — ${t('formularios.f028a.clasificacionAiepi')}`, respuesta: consulta.clasificacionAiepi.trim() },
      ...consulta.diagnosticos
        .map((dx, i) => ({ pregunta: `${prefijo} — ${t('formularios.f028a.diagnostico')} ${i + 1}`, respuesta: textoDiagnosticoConMarca(dx, marcasDiagnostico) }))
        .filter((p) => p.respuesta.trim() !== ''),
      { pregunta: `${prefijo} — ${t('formularios.f028a.planTratamiento')}`, respuesta: consulta.planTratamiento.trim() },
      { pregunta: `${prefijo} — ${t('formularios.f028a.proximaCitaIdeal')}`, respuesta: consulta.proximaCitaIdeal },
    ].filter((p) => p.respuesta.trim() !== '');
  }

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = [
        {
          pregunta: t('formularios.f028a.antecedentes.seccionFamiliares'),
          respuesta: ANTECEDENTES_FAMILIARES.filter((c) => antecedentesFamiliares[c]).map((c) => t(`formularios.f028a.antecedentes.familiares.${c}`)).join(', ') || '—',
        },
        { pregunta: t('formularios.f028a.antecedentes.detalleFamiliares'), respuesta: detalleFamiliares.trim() },
        { pregunta: t('formularios.f028a.antecedentes.datosObstetricos'), respuesta: datosObstetricos.trim() },
        {
          pregunta: t('formularios.f028a.antecedentes.seccionPrenatales'),
          respuesta: [
            gestas && `${t('formularios.f028a.antecedentes.gestas')}: ${gestas}`,
            abortos && `${t('formularios.f028a.antecedentes.abortos')}: ${abortos}`,
            partos && `${t('formularios.f028a.antecedentes.partos')}: ${partos}`,
            cesareas && `${t('formularios.f028a.antecedentes.cesareas')}: ${cesareas}`,
            hijosVivos && `${t('formularios.f028a.antecedentes.hijosVivos')}: ${hijosVivos}`,
            hijosMuertos && `${t('formularios.f028a.antecedentes.hijosMuertos')}: ${hijosMuertos}`,
          ]
            .filter(Boolean)
            .join(' | '),
        },
        ...paresSiNoObs(ANTECEDENTES_NATALES, antecedentesNatales, (c) => `${t('formularios.f028a.antecedentes.seccionNatales')} — ${t(`formularios.f028a.antecedentes.natales.${c}`)}`, textosSiNo),
        { pregunta: t('formularios.f028a.antecedentes.observacionesNatales'), respuesta: observacionesNatales.trim() },
        ...paresSiNoObs(ANTECEDENTES_POSTNATALES_SINO, antecedentesPostnatalesSiNo, (c) => `${t('formularios.f028a.antecedentes.seccionPostnatales')} — ${t(`formularios.f028a.antecedentes.postnatales.${c}`)}`, textosSiNo),
        ...paresSiNoSimples(SI_NO_NACIMIENTO, siNoNacimiento, (c) => t(`formularios.f028a.antecedentes.${c}SiNo`), textosSiNo),
        ...(Object.keys(DATOS_NACIMIENTO_VACIO) as (keyof DatosNacimiento)[])
          .filter((c) => datosNacimiento[c].trim())
          .map((c) => ({ pregunta: t(`formularios.f028a.antecedentes.${c}`), respuesta: datosNacimiento[c].trim() })),
        ...paresDeConsulta(consulta1, 1),
        ...paresDeConsulta(consulta2, 2),
      ].filter((p) => p.respuesta.trim() !== '');

      const lote = new LoteTransaccional();
      for (const consulta of [consulta1, consulta2]) {
        for (const dx of consulta.diagnosticos) {
          if (dx.diagnostico.texto.trim()) {
            lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(dx.diagnostico), recordedDate: new Date().toISOString() });
          }
        }
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.antecedentes.seccionFamiliares')}</h4>
      <ChecklistGrid claves={ANTECEDENTES_FAMILIARES} prefijoI18n="formularios.f028a.antecedentes.familiares" prefijoId="fam" valores={antecedentesFamiliares} onChange={setAntecedentesFamiliares} />
      <AreaDictado id="f028a-detalle-familiares" etiqueta={t('formularios.f028a.antecedentes.detalleFamiliares')} valor={detalleFamiliares} onChange={setDetalleFamiliares} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.antecedentes.seccionPrenatales')}</h4>
      <AreaDictado id="f028a-datos-obstetricos" etiqueta={t('formularios.f028a.antecedentes.datosObstetricos')} valor={datosObstetricos} onChange={setDatosObstetricos} filas={2} />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6">
        <CampoNumero label={t('formularios.f028a.antecedentes.gestas')} value={gestas} onChange={setGestas} />
        <CampoNumero label={t('formularios.f028a.antecedentes.abortos')} value={abortos} onChange={setAbortos} />
        <CampoNumero label={t('formularios.f028a.antecedentes.partos')} value={partos} onChange={setPartos} />
        <CampoNumero label={t('formularios.f028a.antecedentes.cesareas')} value={cesareas} onChange={setCesareas} />
        <CampoNumero label={t('formularios.f028a.antecedentes.hijosVivos')} value={hijosVivos} onChange={setHijosVivos} />
        <CampoNumero label={t('formularios.f028a.antecedentes.hijosMuertos')} value={hijosMuertos} onChange={setHijosMuertos} />
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.antecedentes.seccionNatales')}</h4>
      <ListaSiNoObservacion claves={ANTECEDENTES_NATALES} prefijoI18n="formularios.f028a.antecedentes.natales" prefijoId="natal" valores={antecedentesNatales} onChange={setAntecedentesNatales} />

      <AreaDictado id="f028a-observaciones-natales" etiqueta={t('formularios.f028a.antecedentes.observacionesNatales')} valor={observacionesNatales} onChange={setObservacionesNatales} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.antecedentes.seccionPostnatales')}</h4>
      <ListaSiNoObservacion
        claves={ANTECEDENTES_POSTNATALES_SINO}
        prefijoI18n="formularios.f028a.antecedentes.postnatales"
        prefijoId="postnatal"
        valores={antecedentesPostnatalesSiNo}
        onChange={setAntecedentesPostnatalesSiNo}
      />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SI_NO_NACIMIENTO.map((c) => (
          <SiNoBotones key={c} id={`nac-${c}`} etiqueta={t(`formularios.f028a.antecedentes.${c}SiNo`)} valor={leerSiNo(siNoNacimiento[c])} textos={textosSiNo} onChange={(v) => setSiNoNacimiento((prev) => ({ ...prev, [c]: v }))} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
        {(Object.keys(DATOS_NACIMIENTO_VACIO) as (keyof DatosNacimiento)[]).map((campo) => (
          <CampoNumero
            key={campo}
            label={t(`formularios.f028a.antecedentes.${campo}`)}
            value={datosNacimiento[campo]}
            onChange={(v) => setDatosNacimiento((prev) => ({ ...prev, [campo]: v }))}
          />
        ))}
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028a.seccionAtencion')}</h4>
      <div className={GRID_DOS_CONSULTAS}>
        <BloqueConsulta numero={1} value={consulta1} onChange={setConsulta1} />
        <BloqueConsulta numero={2} value={consulta2} onChange={setConsulta2} />
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
